import React, { useEffect, useMemo, useState } from 'react';

import { Loading } from '../../components/Loading/Loading';
import { Pagination } from '../../components/Pagination/Pagination';
import { pushNotify } from '../../components/Notifications';
import {
  useChangeSettlementStatusMutation,
  useCreateSettlementMutation,
  useGetAnalystFiltersQuery,
  useGetMerchantBalancesQuery,
  useGetPlatformWalletsQuery,
  useGetSettlementsQuery,
} from '../../redux/services/AnalystApi';
import { type AnalystSettlement, type SettlementStatus } from '../../redux/types/AnalystTypes';
import { AnalystPage } from './components/AnalystPage';
import { DIRECTION, SETTLEMENT_STATUS, errorText, fmt } from './helpers';
import styles from './Analyst.module.css';

const PAGE_SIZE = 25;
const ACTION_LABEL: Record<SettlementStatus, string> = {
  IN_PROGRESS: 'Взять в работу',
  COMPLETED: 'Завершить',
  REJECTED: 'Отклонить',
  CREATED: 'Создать',
};

const CreateForm = () => {
  const { data: options } = useGetAnalystFiltersQuery();
  const { data: balances } = useGetMerchantBalancesQuery();
  const { data: wallets } = useGetPlatformWalletsQuery();
  const [create, { isLoading }] = useCreateSettlementMutation();
  const [merchant, setMerchant] = useState('');
  const [direction, setDirection] = useState<'M2P' | 'P2M'>('M2P');
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [platformWallet, setPlatformWallet] = useState('');
  const [comment, setComment] = useState('');

  const balance = useMemo(() => (balances || []).find((b) => String(b.id) === merchant), [balances, merchant]);
  const limit = balance ? (direction === 'M2P' ? balance.available_to_merchant : balance.available_to_platform) : null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!merchant || !amount) {
      pushNotify('Выберите мерчанта и укажите сумму.', 'Сеттлмент');
      return;
    }
    const name = (options?.merchants || []).find((m) => String(m.id) === merchant)?.username;
    if (!window.confirm(`Создать сеттлмент ${DIRECTION[direction]} на ${amount} USDT для ${name}?`)) return;
    const result = await create({
      merchant: Number(merchant),
      direction,
      amount: amount.replace(',', '.'),
      merchant_wallet_address: wallet || undefined,
      platform_wallet: platformWallet || undefined,
      comment: comment || undefined,
    });
    if ('error' in result) {
      pushNotify(errorText(result.error), 'Сеттлмент не создан');
      return;
    }
    pushNotify(`Создан сеттлмент на ${amount} USDT`, 'Сеттлмент');
    setAmount('');
    setComment('');
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <label>
        Мерчант
        <select value={merchant} onChange={(e) => setMerchant(e.target.value)}>
          <option value=''>Выберите мерчанта</option>
          {(options?.merchants || []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.username}
            </option>
          ))}
        </select>
      </label>
      <label>
        Направление
        <select value={direction} onChange={(e) => setDirection(e.target.value as 'M2P' | 'P2M')}>
          <option value='M2P'>{DIRECTION.M2P} (из «площадка должна»)</option>
          <option value='P2M'>{DIRECTION.P2M} (из «мерчант должен»)</option>
        </select>
      </label>
      <label>
        Сумма, USDT {limit !== null && <span>· доступно {fmt(limit, 3)}</span>}
        <input inputMode='decimal' value={amount} onChange={(e) => setAmount(e.target.value)} placeholder='0.000' />
      </label>
      <label>
        Кошелёк мерчанта
        <input value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder='T…' />
      </label>
      <label>
        Кошелёк площадки
        <select value={platformWallet} onChange={(e) => setPlatformWallet(e.target.value)}>
          <option value=''>—</option>
          {(wallets || []).map((w) => (
            <option key={w.id} value={w.id}>
              {w.name} — {w.address}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.formWide}>
        Комментарий (TX hash, основание)
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>
      <div>
        <button type='submit' className={`${styles.button} ${styles.primary}`} disabled={isLoading}>
          {isLoading ? 'Создаём…' : 'Создать сеттлмент'}
        </button>
      </div>
    </form>
  );
};

const Row = ({ s }: { s: AnalystSettlement }) => {
  const [change, { isLoading }] = useChangeSettlementStatusMutation();
  const act = async (status: SettlementStatus) => {
    const comment = window.prompt(`${ACTION_LABEL[status]}: ${s.merchant}, ${fmt(s.amount, 3)} USDT. Комментарий (необязательно):`, '');
    if (comment === null) return;
    const result = await change({ id: s.id, status, comment });
    if ('error' in result) pushNotify(errorText(result.error), 'Статус не изменён');
  };
  return (
    <tr>
      {/* DRF отдаёт дату строкой «ДД-ММ-ГГГГ ЧЧ:ММ» — new Date прочитал бы её как месяц-день */}
      <td>{s.created_at.replace(/-/g, '.')}</td>
      <td>{s.merchant}</td>
      <td>{DIRECTION[s.direction] || s.direction}</td>
      <td className={styles.num}>{fmt(s.amount, 3)}</td>
      <td>
        <span className={`${styles.badge} ${styles[s.status]}`}>{SETTLEMENT_STATUS[s.status] || s.status}</span>
      </td>
      <td className={styles.mono}>{s.merchant_wallet_address || s.platform_wallet_address || '—'}</td>
      <td className={styles.comment}>{s.comment}</td>
      <td>
        <div className={styles.actions}>
          {s.allowed_statuses.map((status) => (
            <button
              key={status}
              type='button'
              disabled={isLoading}
              className={`${styles.button} ${styles.small} ${status === 'REJECTED' ? styles.danger : ''}`}
              onClick={() => act(status)}
            >
              {ACTION_LABEL[status]}
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
};

export const AnalystSettlements = () => {
  const { data: options } = useGetAnalystFiltersQuery();
  const [merchant, setMerchant] = useState('');
  const [status, setStatus] = useState('');
  const [direction, setDirection] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [merchant, status, direction]);
  const { data, isLoading, isError, error } = useGetSettlementsQuery({ page, page_size: PAGE_SIZE, merchant, status, direction });
  const totalPages = Math.max(1, Math.ceil((data?.count || 0) / PAGE_SIZE));

  return (
    <AnalystPage
      title='Сеттлменты'
      hint={
        <>
          Взаиморасчёты с мерчантами. M2P уменьшает «площадка должна мерчанту», P2M — «мерчант должен площадке»; сумма
          резервируется сразу при создании и освобождается, только если сеттлмент отклонить. Переходы: создан → в работе
          или отклонён; в работе → завершён или отклонён. Каждое действие подписывается вашим логином в комментарии.
        </>
      }
    >
      <CreateForm />
      <div className={styles.filters}>
        <select value={merchant} onChange={(e) => setMerchant(e.target.value)}>
          <option value=''>Все мерчанты</option>
          {(options?.merchants || []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.username}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value=''>Все статусы</option>
          {Object.entries(SETTLEMENT_STATUS).map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value=''>Оба направления</option>
          {Object.entries(DIRECTION).map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <span className={styles.note}>{data ? `Найдено: ${data.count}` : ''}</span>
      </div>
      {isError && <div className={styles.error}>{errorText(error)}</div>}
      {isLoading ? (
        <Loading />
      ) : data?.results.length ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Создан</th>
                <th>Мерчант</th>
                <th>Направление</th>
                <th className={styles.num}>Сумма, USDT</th>
                <th>Статус</th>
                <th>Кошелёк</th>
                <th>Комментарий</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((s) => (
                <Row key={s.id} s={s} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.empty}>Сеттлментов нет</div>
      )}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            setCurrentPage={setPage}
            nextPage={data?.next || page}
            previousPage={data?.previous || page}
          />
        </div>
      )}
    </AnalystPage>
  );
};
